/**
 * Utility functions for validating trading days and market hours
 * Market hours: 9:30 AM - 4:00 PM EST (Eastern Time)
 */

/**
 * Get date components in a specific timezone
 */
export function getDateInTimezone(date: Date, timezone: string = 'America/New_York'): { year: number; month: number; day: number; hours: number; minutes: number; dayOfWeek: number } {
  // Always use America/New_York as the default for market hours validation
  const tz = timezone || 'America/New_York';
  
  const options: Intl.DateTimeFormatOptions = {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    weekday: 'short'
  };
  const formatter = new Intl.DateTimeFormat('en-US', options);
  const parts = formatter.formatToParts(date);
  
  const getValue = (type: string) => parts.find(p => p.type === type)?.value || '';
  
  const weekdayStr = getValue('weekday');
  const dayOfWeekMap: Record<string, number> = { 'Sun': 0, 'Mon': 1, 'Tue': 2, 'Wed': 3, 'Thu': 4, 'Fri': 5, 'Sat': 6 };
  
  const result = {
    year: parseInt(getValue('year')),
    month: parseInt(getValue('month')),
    day: parseInt(getValue('day')),
    hours: parseInt(getValue('hour')),
    minutes: parseInt(getValue('minute')),
    dayOfWeek: dayOfWeekMap[weekdayStr] ?? date.getDay()
  };
  return result;
}

/**
 * Check if a date is a weekend (Saturday or Sunday)
 */
export function isWeekend(date: Date, timezone: string = 'America/New_York'): boolean {
  const { dayOfWeek } = getDateInTimezone(date, timezone);
  return dayOfWeek === 0 || dayOfWeek === 6; // 0 = Sunday, 6 = Saturday
}

/**
 * US Market Holidays (2024-2025)
 * Note: This should be updated annually or fetched from an API
 */
const US_MARKET_HOLIDAYS = [
  // 2024
  '2024-01-01', // New Year's Day
  '2024-01-15', // MLK Jr. Day
  '2024-02-19', // Presidents' Day
  '2024-03-29', // Good Friday
  '2024-05-27', // Memorial Day
  '2024-06-19', // Juneteenth
  '2024-07-04', // Independence Day
  '2024-09-02', // Labor Day
  '2024-11-28', // Thanksgiving
  '2024-12-25', // Christmas
  // 2025
  '2025-01-01', // New Year's Day
  '2025-01-20', // MLK Jr. Day
  '2025-02-17', // Presidents' Day
  '2025-04-18', // Good Friday
  '2025-05-26', // Memorial Day
  '2025-06-19', // Juneteenth
  '2025-07-04', // Independence Day
  '2025-09-01', // Labor Day
  '2025-11-27', // Thanksgiving
  '2025-12-25', // Christmas
];

/**
 * Check if a date is a US market holiday
 */
export function isMarketHoliday(date: Date, timezone: string = 'America/New_York'): boolean {
  // Use timezone-aware date components to avoid conversion issues
  const { year, month, day } = getDateInTimezone(date, timezone);
  const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  return US_MARKET_HOLIDAYS.includes(dateStr);
}

/**
 * Check if a date is a valid trading day (not weekend, not holiday)
 */
export function isTradingDay(date: Date, timezone: string = 'America/New_York'): boolean {
  return !isWeekend(date, timezone) && !isMarketHoliday(date, timezone);
}

/**
 * Get market hours in EST timezone
 * Returns { open: Date, close: Date } for the given date
 */
export function getMarketHours(date: Date): { open: Date; close: Date } {
  // Create dates in EST timezone
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  
  // Market opens at 9:30 AM EST
  const open = new Date(year, month, day, 9, 30, 0);
  
  // Market closes at 4:00 PM EST (16:00)
  const close = new Date(year, month, day, 16, 0, 0);
  
  return { open, close };
}

/**
 * Check if a time is within market hours (9:30 AM - 4:00 PM in the specified timezone)
 */
export function isWithinMarketHours(date: Date, timezone: string = 'America/New_York'): boolean {
  const { hours, minutes } = getDateInTimezone(date, timezone);
  
  // Before 9:30 AM
  if (hours < 9 || (hours === 9 && minutes < 30)) {
    return false;
  }
  
  // After 4:00 PM
  if (hours >= 16) {
    return false;
  }
  
  return true;
}

/**
 * Adjust a date to the nearest valid trading time
 * If outside market hours, snap to market open or close
 * If on weekend/holiday, move to next trading day at market open
 */
export function adjustToValidTradingTime(date: Date): Date {
  const adjusted = new Date(date);
  
  // First, ensure it's a trading day
  while (!isTradingDay(adjusted)) {
    adjusted.setDate(adjusted.getDate() + 1);
  }
  
  // Then, ensure it's within market hours
  const { open, close } = getMarketHours(adjusted);
  
  if (adjusted < open) {
    return open;
  } else if (adjusted > close) {
    return close;
  }
  
  return adjusted;
}

/**
 * Get the next valid trading day
 */
export function getNextTradingDay(date: Date): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + 1);
  
  while (!isTradingDay(next)) {
    next.setDate(next.getDate() + 1);
  }
  
  return next;
}

/**
 * Get the previous valid trading day
 */
export function getPreviousTradingDay(date: Date): Date {
  const prev = new Date(date);
  prev.setDate(prev.getDate() - 1);
  
  while (!isTradingDay(prev)) {
    prev.setDate(prev.getDate() - 1);
  }
  
  return prev;
}

/**
 * Format time for display in EST timezone
 */
export function formatTimeEST(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    timeZone: 'America/New_York',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
}

/**
 * Format date for display
 */
export function formatDateDisplay(date: Date): string {
  return date.toLocaleDateString('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

/**
 * Check if the market is currently open
 * Takes into account weekends, holidays, and market hours (9:30 AM - 4:00 PM EST)
 */
export function isMarketOpen(date: Date = new Date()): boolean {
  // Convert to EST for accurate market hours check
  const estDate = new Date(date.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  
  // Check if it's a trading day
  if (!isTradingDay(estDate)) {
    return false;
  }
  
  // Check if within market hours
  return isWithinMarketHours(estDate);
}

export const MARKET_SESSION_MINUTES = {
  PRE_MARKET_START: 7 * 60,
  MARKET_OPEN: 9 * 60 + 30,
  MARKET_CLOSE: 16 * 60,
  AFTER_HOURS_END: 20 * 60,
} as const;

/**
 * Get current market session status
 * Returns: 'pre-market' | 'regular' | 'after-hours' | 'closed'
 */
export function getMarketSession(date: Date = new Date()): 'pre-market' | 'regular' | 'after-hours' | 'closed' {
  // Convert to EST for accurate market hours check
  const estDate = new Date(date.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  
  // Check if it's a trading day
  if (!isTradingDay(estDate)) {
    return 'closed';
  }
  
  const hours = estDate.getHours();
  const minutes = estDate.getMinutes();
  const timeInMinutes = hours * 60 + minutes;

  const { PRE_MARKET_START, MARKET_OPEN, MARKET_CLOSE, AFTER_HOURS_END } = MARKET_SESSION_MINUTES;

  if (timeInMinutes >= PRE_MARKET_START && timeInMinutes < MARKET_OPEN) {
    return 'pre-market';
  } else if (timeInMinutes >= MARKET_OPEN && timeInMinutes < MARKET_CLOSE) {
    return 'regular';
  } else if (timeInMinutes >= MARKET_CLOSE && timeInMinutes < AFTER_HOURS_END) {
    return 'after-hours';
  }
  
  return 'closed';
}
